import { useId, useState, type FormEvent } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Calculator, Phone } from 'lucide-react';
import { calculateDelivery, type DeliveryCalculation } from '@/api/pricing';
import { ApiError } from '@/api/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useContact } from '@/features/settings/useSiteSettings';
import { cn, formatForint } from '@/lib/utils';

const Row = ({ label, value }: { label: string; value: string }) => (
  <li className="flex justify-between gap-4 border-b border-cream/15 py-2.5 text-base">
    <span className="text-cream/80">{label}</span>
    <span className="text-right font-semibold text-cream">{value}</span>
  </li>
);

const Result = ({ result }: { result: DeliveryCalculation }) => {
  const contact = useContact();
  if (!result.found || result.deliveryFee === null) {
    return (
      <div>
        <p className="font-display text-lg font-bold text-cream">{result.place}</p>
        <p className="mt-2 text-cream/85">{result.message}</p>
        <a
          href={contact.phoneHref}
          className="mt-4 inline-flex min-h-12 items-center gap-2.5 rounded-md border-2 border-teal bg-teal px-5 font-semibold text-navy-deep no-underline hover:bg-teal-light"
        >
          <Phone className="size-5" aria-hidden="true" />
          {contact.phone}
        </a>
      </div>
    );
  }

  return (
    <div>
      <p className="flex flex-wrap items-baseline justify-between gap-2">
        <span className="font-display text-lg font-bold text-cream">{result.place}</span>
        {result.county && <span className="text-sm text-cream/70">{result.county}</span>}
      </p>

      <ul className="mt-3 list-none p-0">
        <Row
          label="Távolság a telephelytől"
          value={`${result.distanceKm} km${result.roundTrip ? ` (oda-vissza ${result.chargedKm} km)` : ''}`}
        />
        <Row label={`Alapdíj (${result.freeKm} km-ig)`} value={formatForint(result.baseFee)} />
        <Row label={`${result.chargeableKm} km × ${formatForint(result.pricePerKm)}`} value={formatForint(result.kmFee ?? 0)} />
      </ul>

      <p className="mt-4 flex flex-wrap items-baseline justify-between gap-2">
        <span className="text-cream/80">Szállítási díj</span>
        <span className="font-display text-3xl font-bold text-cream">{formatForint(result.deliveryFee)}</span>
      </p>

      <p
        className={cn(
          'mt-4 rounded-md px-3 py-2 text-sm',
          !result.insideServiceArea ? 'bg-warning/25 text-cream' : result.estimated ? 'bg-teal/25 text-cream' : 'text-cream/70'
        )}
      >
        {result.message}
      </p>
    </div>
  );
};

export interface DeliveryCalculatorProps {
  compact?: boolean;
  className?: string;
}

export const DeliveryCalculator = ({ compact = false, className }: DeliveryCalculatorProps) => {
  const inputId = useId();
  const [query, setQuery] = useState('');
  const [inputError, setInputError] = useState<string | null>(null);

  const calculation = useMutation({
    mutationFn: (value: string) => calculateDelivery(value)
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const value = query.trim();

    if (value.length < 2) {
      setInputError('Kérjük, adja meg az irányítószámot vagy a település nevét.');
      return;
    }

    setInputError(null);
    calculation.mutate(value);
  };

  const serverError =
    calculation.error instanceof ApiError
      ? calculation.error.issues[0]?.message ?? calculation.error.message
      : calculation.error
        ? 'A számítás most nem érhető el. Kérjük, próbálja újra, vagy hívjon minket.'
        : null;

  return (
    <div className={cn('rounded-xl bg-paper', compact ? 'p-5' : 'p-7 sm:p-10', className)}>
      <form onSubmit={submit} noValidate className="flex flex-col gap-3 sm:flex-row sm:items-end">
        <div className="flex flex-1 flex-col gap-2">
          <Label htmlFor={inputId}>Irányítószám vagy település</Label>
          <Input
            id={inputId}
            type="text"
            inputMode="text"
            autoComplete="postal-code"
            placeholder="pl. 8800 vagy Nagykanizsa"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            invalid={Boolean(inputError)}
            aria-describedby={inputError ? `${inputId}-error` : undefined}
          />
        </div>
        <Button type="submit" size="lg" loading={calculation.isPending} loadingText="Számolás…">
          <Calculator className="size-5" aria-hidden="true" />
          Kiszámítom
        </Button>
      </form>

      {inputError && (
        <p id={`${inputId}-error`} role="alert" className="mt-2 text-sm font-semibold text-danger">
          {inputError}
        </p>
      )}

      {!compact && (
        <p className="mt-4 text-sm text-ink-soft">
          A díj a telephelyünktől mért távolság alapján számolódik, egyszeri díjként. Nem kell megadnia semmilyen
          személyes adatot.
        </p>
      )}

      <div className="mt-5 rounded-lg bg-navy p-5 text-cream sm:p-6" aria-live="polite">
        <p className="text-sm font-semibold text-cream/70">Várható szállítási költség</p>

        {serverError && (
          <p role="alert" className="mt-2 text-cream">
            {serverError}
          </p>
        )}

        {!serverError && calculation.data && (
          <div className="mt-2">
            <Result result={calculation.data} />
          </div>
        )}

        {!serverError && !calculation.data && (
          <p className="mt-2 text-cream/70">Írja be az irányítószámot vagy a település nevét, és itt azonnal megjelenik az összeg.</p>
        )}
      </div>
    </div>
  );
};
